/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * «Jamaat-tider» on Bønnetider (owner, 2026-10-15): the mosque's jamaat times as periods — its own, and IRN's when the
 * mosque has them there. Own periods are added, changed and deleted here (prayer.write, as a day's override); an IRN
 * period is corrected by an own period for the same days, which wins. The next 14 days show begins and jamaat side by
 * side, with where each jamaat came from; what needs a look (a jamaat before its prayer begins or after its time is out
 * — audit F-01 — an IRN period with fixed times across a clock change, days with no period) is said first. Rules: core jamaat-rules.js, through core.ts; the server checks again.
 *
 * The next 14 days are a GRID (work hub, 2026-10-31): rows are days, a cell per prayer (Jumu'ah in Dhuhr's place on a
 * Friday), begins greyed beside it. Typing a time saves a per-day override through the existing override API (the same
 * rules: not before the prayer begins, not after its time is out — checked here by core jamaat-grid, and again by the
 * server); a changed cell says «endret» and has «Angre» (the override for that cell is taken away). Tab and Enter move
 * like a spreadsheet; pasting a column of times (one per line) fills down.
 */
import React, { useRef, useState } from 'react';
import { JamaatGrid, type GridRow } from '../core';
import { errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { osloTime, shortDate } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { fetchIrnNow } from './TimetableSource';
import { FIELDS, blankForm, bodyFromForm, check, formFromPeriod, SOURCE_NO, summary, type JamaatForm } from '../lib/jamaat';
import { JumuaFixedSection } from './JumuaFixed';

const PRAYERS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
const PATH = '/prayer-times/jamaat';

/** `YYYY-MM` of a date, and the month before or after one. */
const monthOf = (iso: string) => iso.slice(0, 7);
const shiftMonth = (m: string, by: number) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const MONTH_NO = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
const monthName = (m: string) => `${MONTH_NO[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

export function JamaatCard({ onChanged }: { onChanged?: () => void }) {
  // The grid shows the next 14 days, or a whole month (owner 2026-11-01: «show for whole month»).
  const [month, setMonth] = useState<string | null>(null);
  const { data, error, reload } = useLoad<Body>(month ? `${PATH}?month=${month}` : PATH);
  const [form, setForm] = useState<JamaatForm | null>(null);
  const [bad, setBad] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const card = useRef<HTMLDivElement>(null);
  // Owner 2026-10-23: the period form opens in a popup. What it opened with, so closing asks only after a change.
  const opened = useRef('');
  useDirty('jamaat', Boolean(form));
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const periods: Body[] = (data.periods || []).filter((p: Body) => !p.past);
  const past = (data.periods || []).length - periods.length;
  const write = Boolean(data.canWrite);
  const open = (f: JamaatForm) => { opened.current = JSON.stringify(f); setForm(f); setBad({}); say(''); };
  const cancel = () => { setForm(null); setBad({}); say('Avbrutt. Ingenting er lagret.'); };
  const done = async (text: string) => { setForm(null); setBad({}); say(text, 'ok'); await reload(); onChanged?.(); };

  async function save() {
    if (!form) return;
    const c = check(form);
    if (!c.ok) { setBad(c.byField); return say(c.errors[0].message, 'err'); }
    setBusy(true);
    const r = form.id ? await send(`${PATH}/${encodeURIComponent(form.id)}`, { method: 'PUT', body: JSON.stringify(bodyFromForm(form)) })
      : await post(PATH, bodyFromForm(form));
    setBusy(false);
    if (!r.ok) {
      const errs: Body[] = Array.isArray(r.body && r.body.errors) ? r.body.errors : [];
      setBad(Object.fromEntries(errs.map((e) => [e.field, e.message])));
      return say((r.body && r.body.message) || errText(r.body), 'err');
    }
    const warn = (r.body.warnings || []).map((w: Body) => w.message).join(' ');
    await done(`Lagret. Appen, skjermene og nettsiden viser tidene ved neste oppdatering.${warn ? ' ' + warn : ''}`);
  }

  async function remove(p: Body) {
    const r = await send(`${PATH}/${encodeURIComponent(p.id)}`, { method: 'DELETE' });
    if (!r.ok) return say(errText(r.body), 'err');
    await done(`Perioden ${shortDate(p.startsOn)} – ${shortDate(p.endsOn)} er slettet.`);
  }

  const warnings: Body[] = data.warnings || [];
  return (
    <div ref={card}>
      <Card title="Jamaat-tider" id="jamaat-card"
            hint="Når menigheten ber sammen. Skriv et klokkeslett (07:15) eller minutter etter at bønnen begynner (+5)."
            more={<>En periode gjelder fra og med én dato til og med en annen. Egne perioder går foran IRN sine, og en overstyring
              for én dag går foran begge. Jamaat vises aldri før bønnen begynner. Jumu'ah gjelder bare fredager.</>}>
        <CardBody>
          {warnings.length > 0 && (
            <ul className="jm-warnings" aria-label="Se over">
              {warnings.map((w, i) => <li key={i} className="warnc">{w.message_no}</li>)}
            </ul>
          )}
          <table className="t jm-periods">
            <thead><tr><th scope="col">Periode</th><th scope="col">Tider</th><th scope="col">Kilde</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>
              {periods.length ? periods.map((p) => (
                <tr key={p.id}>
                  <td>{shortDate(p.startsOn)} – {shortDate(p.endsOn)}{p.label ? <div className="note">{p.label}</div> : null}</td>
                  <td>{summary(p)}
                    {p.beforeBegins && <div><Tag tone="red" title={`Første ${p.beforeBegins.first}`}>{p.beforeBegins.days} {p.beforeBegins.days === 1 ? 'dag' : 'dager'} før bønnen begynner — vises ikke</Tag></div>}
                    {p.afterWindow && <div><Tag tone="red" title={`Første ${p.afterWindow.first}`}>{p.afterWindow.days} {p.afterWindow.days === 1 ? 'dag' : 'dager'} etter at bønnetiden er ute — vises ikke</Tag></div>}</td>
                  <td>{p.source === 'irn' ? <Tag tone="grey">IRN</Tag> : <Tag tone="green">egen</Tag>}</td>
                  <td>{write && (p.source === 'staff'
                    ? <><Btn ghost onClick={() => open(formFromPeriod(p))}>Endre</Btn>{' '}
                        <TwoStep label="Slett" confirmLabel="Klikk igjen for å slette" onConfirm={() => remove(p)} /></>
                    : <Btn ghost title="Lag en egen periode med de samme dagene — den går foran IRN sin." onClick={() => open(formFromPeriod(p, true))}>Rett</Btn>)}</td>
                </tr>
              )) : <tr><td colSpan={4}>Ingen jamaat-tider ennå. Appen viser når bønnene begynner.</td></tr>}
            </tbody>
          </table>
          {past > 0 && <p className="note">{past} tidligere {past === 1 ? 'periode' : 'perioder'} vises ikke.</p>}
          {periods.some((p) => p.source === 'irn') && <p className="note jm-irn">IRN-tidene hentes daglig{data.irnCheckedAt ? `, sist ${osloTime(data.irnCheckedAt)}` : ''}. De endres hos IRN.</p>}
          {write && <div className="row" style={{ marginTop: 12 }}><Btn onClick={() => open(blankForm())}>+ Legg til periode</Btn></div>}
          {/* «Fast Jumu'ah-tid» (owner 2026-11-01): every Friday, below every period — views/JumuaFixed.tsx. */}
          <JumuaFixedSection fixed={data.fixedJumua || null} write={write} onSaved={async () => { await reload(); onChanged?.(); }} />
          <FormDialog open={write && Boolean(form)} title={form && form.id ? 'Endre periode' : 'Ny periode'}
                      dirty={Boolean(form) && JSON.stringify(form) !== opened.current} onClose={cancel}>
            {form && <PeriodForm form={form} bad={bad} busy={busy} change={(f) => { setForm(f); setBad({}); }} save={save} cancel={cancel} />}
            <Msg text={msg.text} tone={msg.tone} />
          </FormDialog>
          {!form && <Msg text={msg.text} tone={msg.tone} />}
        </CardBody>
      </Card>
      <Grid rows={(data.grid || []) as GridRow[]} write={write} onSaved={async () => { await reload(); onChanged?.(); }}
            month={month} today={String(data.today || '')} lastDay={data.lastDay || null} setMonth={setMonth}
            irn={Boolean(data.canFetchIrn)} onFetched={async () => { await reload(); onChanged?.(); }} />
    </div>
  );
}

const NAME: Record<string, string> = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha', jumua: "Jumu'ah" };
const cellId = (r: number, c: number) => `jg-${r}-${c}`;

/**
 * The 14 days, or a whole month, as an editable grid: a typed time is a per-day override; «Angre» takes a cell's override
 * away. ‹ › step a month; «Hent fra IRN» runs the same IRN fetch as Bønnetidskilde's «Hent fra IRN nå» (prayer times and
 * the mosque's jamaat periods at IRN; a month somebody rolled back comes back waiting for approval there).
 */
function Grid({ rows, write, onSaved, month, today, lastDay, setMonth, irn, onFetched }: {
  rows: GridRow[]; write: boolean; onSaved: () => Promise<void>;
  month: string | null; today: string; lastDay: string | null; setMonth: (m: string | null) => void;
  irn: boolean; onFetched: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const key = (r: number, c: number) => `${r}:${c}`;
  const cellOf = (r: number, c: number) => rows[r] && rows[r].cells[PRAYERS[c]];
  /** Save one cell's value (or clear its override). → whether it went through. */
  async function save(r: number, c: number, raw: string, quiet = false): Promise<boolean> {
    const row = rows[r], cell = cellOf(r, c);
    if (!row || !cell || row.missing || row.isClosed) return false;
    const typed = raw.trim();
    if (typed === (cell.jamaat || '') || (!typed && !cell.overridden)) return false;
    if (!typed) return undo(r, c);
    const v = JamaatGrid.cellInput(row.day, cell.prayer, typed, rows[r + 1] && rows[r + 1].day);
    if (!v.ok) { say(`${shortDate(row.date)} ${NAME[cell.prayer]}: ${v.message}`, 'err'); return false; }
    setBusy(true);
    const res = await post('/prayer-times/override', { date: row.date, patch: v.patch, reason: (row.override && row.override.reason) || 'other' });
    setBusy(false);
    if (!res.ok) { say(`${shortDate(row.date)} ${NAME[cell.prayer]}: ${(res.body && (res.body.errors && res.body.errors[0] && res.body.errors[0].message)) || errText(res.body)}`, 'err'); return false; }
    if (!quiet) say(`${NAME[cell.prayer]} ${shortDate(row.date)}: jamaat ${v.value}. Appen og skjermene viser det ved neste oppdatering.`, 'ok');
    return true;
  }
  async function commit(r: number, c: number) {
    const k = key(r, c);
    if (draft[k] === undefined) return;
    const ok = await save(r, c, draft[k]);
    setDraft((d) => { const n = { ...d }; delete n[k]; return n; });
    if (ok) await onSaved();
  }
  async function undo(r: number, c: number): Promise<boolean> {
    const row = rows[r], cell = cellOf(r, c);
    if (!row || !cell || !row.override) return false;
    const left = JamaatGrid.remaining({ ...Object.fromEntries(row.override.fields.map((f) => [f, 'x'])), is_closed: row.override.isClosed }, cell.key);
    setBusy(true);
    const res = left.empty ? await send(`/prayer-times/override/${row.date}`, { method: 'DELETE' })
      : await post('/prayer-times/override', { date: row.date, patch: { [cell.key]: null }, reason: row.override.reason || 'other' });
    setBusy(false);
    if (!res.ok) { say(errText(res.body), 'err'); return false; }
    say(`${NAME[cell.prayer]} ${shortDate(row.date)}: endringen er angret.`, 'ok');
    await onSaved();
    return true;
  }
  async function paste(r: number, c: number, text: string) {
    const values = JamaatGrid.pasteColumn(text);
    let saved = 0;
    for (let i = 0; i < values.length && r + i < rows.length; i++) {
      const v = values[i];
      if (v && await save(r + i, c, v, true)) saved++;
    }
    setDraft({});
    if (saved) { say(`${saved} ${saved === 1 ? 'tid er lagret' : 'tider er lagret'} nedover i ${NAME[PRAYERS[c]]}-kolonnen.`, 'ok'); await onSaved(); }
  }
  function keys(e: React.KeyboardEvent<HTMLInputElement>, r: number, c: number) {
    if (e.key !== 'Tab' && e.key !== 'Enter') return;
    const to = JamaatGrid.move(r, c, e.key, rows.length, { shift: e.shiftKey });
    if (!to) return;
    e.preventDefault();
    // Moving focus blurs this cell, and onBlur commits it — once (review P2: committing here too saved it twice).
    (document.getElementById(cellId(to.row, to.col)) as HTMLInputElement | null)?.focus();
  }
  const [fetching, setFetching] = useState(false);
  async function fetchIrn() {
    setFetching(true);
    const r = await fetchIrnNow();
    setFetching(false);
    if (!r.ok) return say((r.body && r.body.message) || errText(r.body), 'err');
    const c = r.body.counts || {};
    const parts = [[c.published, 'publisert'], [c.held, 'venter på godkjenning i Bønnetidskilde'], [c.unchanged, 'uendret'], [c.failed, 'feilet']]
      .filter(([n]) => n).map(([n, w]) => `${n} ${n === 1 ? 'måned' : 'måneder'} ${w}`);
    say(`Hentet fra IRN: ${parts.join(' · ') || 'ingenting nytt'}.`, r.body.ok ? 'ok' : 'err');
    await onFetched();
  }
  const shown = month || monthOf(today || new Date().toISOString());
  const empty = rows.length > 0 && rows.every((d) => d.missing);
  return (
    <Card title={month ? `Jamaat-tider ${monthName(month)}` : 'Neste 14 dager'}
          hint={write ? 'Skriv en jamaat-tid i en rute for å endre bare den dagen. Tab og Enter flytter som i et regneark; lim inn en kolonne med tider for å fylle nedover.'
      : 'Når bønnen begynner, og jamaat — slik appen og skjermene viser det.'}>
      <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Btn ghost aria-label="Forrige måned" onClick={() => setMonth(shiftMonth(shown, -1))}>‹</Btn>
        <Btn ghost={month !== null} onClick={() => setMonth(null)}>Neste 14 dager</Btn>
        <Btn ghost={month === null} onClick={() => setMonth(monthOf(today))}>Hele måneden</Btn>
        <Btn ghost aria-label="Neste måned" onClick={() => setMonth(shiftMonth(shown, 1))}>›</Btn>
        {irn && <Btn ghost disabled={fetching} onClick={() => void fetchIrn()}>{fetching ? 'Henter …' : 'Hent fra IRN'}</Btn>}
      </div>
      {empty && <p className="note" role="status">Ingen bønnetider for {month ? monthName(month) : 'disse dagene'} ennå
        {lastDay ? ` (bønnetidene går til ${shortDate(lastDay)})` : ''}. {irn ? 'Trykk «Hent fra IRN», og godkjenn måneden i Bønnetidskilde hvis den venter.' : 'Bønnetidene kommer fra Bønnetidskilde.'}</p>}
      <table className="t jm-preview jm-grid" aria-busy={busy || undefined}>
        <thead><tr><th scope="col">Dato</th>{PRAYERS.map((p) => <th scope="col" key={p}>{NAME[p]}</th>)}</tr></thead>
        <tbody>
          {rows.map((d, r) => (
            <tr key={d.date}>
              <th scope="row">{shortDate(d.date)}{d.isClosed ? <> <Tag tone="red">stengt</Tag></> : null}</th>
              {d.missing ? <td colSpan={5} className="note">Ingen bønnetider for dagen.</td> : PRAYERS.map((p, c) => {
                const cell = d.cells[p];
                const k = key(r, c);
                const label = `${NAME[cell.prayer]} ${shortDate(d.date)}`;
                return (
                  <td key={p}>
                    <div className="note" style={{ fontSize: 12 }}>begynner {cell.begins || '—'}</div>
                    {write && !d.isClosed ? (
                      <input id={cellId(r, c)} aria-label={`${label}, jamaat`} inputMode="numeric" autoComplete="off" maxLength={5} style={{ maxWidth: 72 }}
                        placeholder="—" value={draft[k] ?? (cell.jamaat || '')}
                        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} onBlur={() => void commit(r, c)} onKeyDown={(e) => keys(e, r, c)}
                        onPaste={(e) => { const t = e.clipboardData.getData('text'); if (/\n|;/.test(t.trim())) { e.preventDefault(); void paste(r, c, t); } }} />
                    ) : <div>{cell.jamaat ? `jamaat ${cell.jamaat}` : '—'}</div>}
                    {cell.source && cell.source !== 'override' && cell.source !== 'timetable' && <div className="note" style={{ fontSize: 12 }}>{SOURCE_NO[cell.source] || cell.source}</div>}
                    {cell.overridden && <div style={{ fontSize: 12 }}><Tag tone="gold">endret</Tag>{write && <> <Btn ghost aria-label={`Angre endringen for ${label}`} onClick={() => undo(r, c)}>Angre</Btn></>}</div>}
                    {cell.flag && <div className="warnc" style={{ fontSize: 12 }}>{cell.flag.code === 'after_window' ? `${cell.flag.value} er etter at tiden er ute` : `${cell.flag.value} er før bønnen begynner`}</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <Msg text={msg.text} tone={msg.tone} />
    </Card>
  );
}

function PeriodForm({ form, bad, busy, change, save, cancel }: {
  form: JamaatForm; bad: Record<string, string>; busy: boolean; change: (f: JamaatForm) => void; save: () => void; cancel: () => void;
}) {
  const set = (patch: Partial<JamaatForm>) => change({ ...form, ...patch });
  return (
    <fieldset className="jm-form" aria-label={form.id ? 'Endre periode' : 'Ny periode'}>
      <div className="row">
        <Field label="Fra og med" required className="field half">{(id) =>
          <input id={id} type="date" value={form.from} aria-invalid={Boolean(bad.starts_on) || undefined} onChange={(e) => set({ from: e.target.value })} />}</Field>
        <Field label="Til og med" required className="field half">{(id) =>
          <input id={id} type="date" value={form.to} aria-invalid={Boolean(bad.ends_on) || undefined} onChange={(e) => set({ to: e.target.value })} />}</Field>
        <Field label="Navn (valgfritt)" className="field half" note="Bare i panelet, f.eks. «Vinter».">{(id, d) =>
          <input id={id} type="text" maxLength={60} aria-describedby={d} value={form.label} onChange={(e) => set({ label: e.target.value })} />}</Field>
      </div>
      <div className="row">
        {FIELDS.map((f) => (
          <Field key={f.key} label={f.label} style={{ maxWidth: 130 }} note={bad[f.key] || undefined}>{(id, d) =>
            <input id={id} type="text" inputMode="text" autoComplete="off" placeholder={f.fixedOnly ? '13:30' : '07:15 / +5'}
                   aria-describedby={d} aria-invalid={Boolean(bad[f.key]) || undefined} value={form.values[f.key] || ''}
                   onChange={(e) => set({ values: { ...form.values, [f.key]: e.target.value } })} />}</Field>
        ))}
      </div>
      <p className="note" style={{ margin: '0 0 8px' }}>Tom = ingen jamaat fra denne perioden. Khutbah og Jumu'ah gjelder fredager.</p>
      <div className="row">
        <Btn onClick={save} disabled={busy}>{busy ? 'Lagrer …' : 'Lagre'}</Btn>
        <Btn ghost onClick={cancel}>Avbryt</Btn>
      </div>
    </fieldset>
  );
}
